<template>
  <div
    class="locale-selector"
    :class="`variant-${props.variant}`"
    ref="selectorRef"
  >
    <div
      class="select-trigger"
      :class="[`variant-${props.variant}`, { active: isOpen }]"
      @click="toggleDropdown"
    >
      <div class="trigger-content">
        <img
          :src="`/images/localization/${selectedLocale.toLowerCase()}-flag.svg`"
          :alt="`${selectedLocale} flag`"
          class="flag-icon"
        />
        <span class="select-text">{{ selectedLocale }}</span>
      </div>
      <svg
        class="dropdown-arrow"
        :class="{ rotated: isOpen }"
        width="12"
        height="8"
        viewBox="0 0 12 8"
        fill="none"
      >
        <path
          d="M1 1.5L6 6.5L11 1.5"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>

    <Transition name="dropdown">
      <div
        v-if="isOpen"
        class="select-dropdown"
        :class="[
          `variant-${props.variant}`,
          { 'drop-up': isDropUp, 'right-aligned': isRightAligned },
        ]"
      >
        <div class="options-container">
          <div
            v-for="locale in allLocales"
            :key="locale.value"
            class="select-option"
            :class="{
              selected: locale.value === selectedLocale,
              disabled: isLocaleDisabled(locale.value),
            }"
            @click="
              !isLocaleDisabled(locale.value) && selectLocale(locale.value)
            "
          >
            <div class="option-content">
              <div class="locale-info">
                <img
                  :src="`/images/localization/${locale.value.toLowerCase()}-flag.svg`"
                  :alt="`${locale.value} flag`"
                  class="flag-icon"
                />
                <span class="option-text">{{ locale.text }}</span>
              </div>
              <svg
                v-if="locale.value === selectedLocale"
                class="checkmark-icon"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="10" cy="10" r="10" fill="#003D6A" />
                <path
                  d="M6 10L8.5 12.5L14 7"
                  stroke="white"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { useLocaleStore } from "@/store/locale";
import {
  computed,
  defineEmits,
  defineProps,
  onMounted,
  onUnmounted,
  ref,
} from "vue";

const props = defineProps({
  modelValue: {
    type: String,
    default: "US",
  },
  isSingaporeDisabled: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String,
    default: "default",
    validator: (value) =>
      ["default", "dark", "form", "bordered"].includes(value),
  },
});

const emit = defineEmits(["update:modelValue"]);

const localeStore = useLocaleStore();

const selectorRef = ref(null);
const isOpen = ref(false);
const isDropUp = ref(false);
const isRightAligned = ref(false);

const selectedLocale = computed(() => props.modelValue);

const allLocales = computed(() => localeStore.availableLocales);

const calculatePosition = () => {
  if (!selectorRef.value) return;
  const trigger = selectorRef.value.querySelector(".select-trigger");
  if (!trigger) return;

  const rect = trigger.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const maxHeight = 250;
  const minWidthRequired = 360;

  // Vertical
  const spaceBelow = viewportHeight - rect.bottom;
  const spaceAbove = rect.top;
  isDropUp.value = spaceBelow < maxHeight && spaceAbove > spaceBelow;

  // Horizontal
  const spaceRight = viewportWidth - rect.left;
  isRightAligned.value = spaceRight < minWidthRequired;
};

const toggleDropdown = () => {
  if (!isOpen.value) {
    calculatePosition();
  }
  isOpen.value = !isOpen.value;
};

const isLocaleDisabled = (locale) => {
  return props.isSingaporeDisabled && locale === "SG";
};

const selectLocale = (locale) => {
  if (isLocaleDisabled(locale)) {
    return;
  }
  if (locale !== selectedLocale.value) {
    // Update the store
    localeStore.setLocale(locale);
    emit("update:modelValue", locale);
  }
  isOpen.value = false;
};

const handleClickOutside = (event) => {
  if (selectorRef.value && !selectorRef.value.contains(event.target)) {
    isOpen.value = false;
  }
};

onMounted(async () => {
  document.addEventListener("click", handleClickOutside);

  // Always initialize the locale - this will check IP and update if needed
  if (!localeStore.isInitialized) {
    await localeStore.initializeLocale();
  }

  // Sync with the store's current locale
  if (localeStore.currentLocale !== props.modelValue) {
    emit("update:modelValue", localeStore.currentLocale);
  }
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>

<style scoped>
.locale-selector {
  position: relative;
  width: fit-content;
}

.select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid var(--neutral-700);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 40px;
}

.select-trigger:hover {
  border-color: var(--blue-dark-2);
}

.select-trigger.active {
  border-color: var(--blue-dark-2);
  background-color: var(--blue-dark-2);
}

.select-trigger.active .select-text,
.select-trigger.active .dropdown-arrow {
  color: #ffffff;
}

.trigger-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.select-text {
  font-size: var(--fs-big);
  font-weight: 600;
  color: var(--primary-blue);
  flex: 1;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.flag-icon {
  width: 20px;
  height: 20px;
  object-fit: cover;
  border-radius: 50%;
  flex-shrink: 0;
  overflow: hidden;
}

.dropdown-arrow {
  flex-shrink: 0;
  color: #6b7280;
  transition: transform 0.2s ease;
}

.dropdown-arrow.rotated {
  transform: rotate(180deg);
}

.select-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  z-index: 1001;
  min-width: 360px;
  height: auto;
  padding: 8px;
}

.select-dropdown.right-aligned {
  left: auto !important;
  right: 0;
}

.select-dropdown.drop-up {
  top: auto;
  bottom: calc(100% + 4px);
  margin-bottom: 0;
}

.options-container {
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
}

.select-option {
  display: flex;
  align-items: center;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.select-option:hover:not(.disabled) {
  background-color: #f9fafb;
}

.select-option.selected {
  background-color: #f2f3fb;
  color: var(--primary-blue);
  border-radius: 12px;
}

.select-option.selected:hover {
  background-color: #e8e7f5;
}

.select-option.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.option-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
}

.locale-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.option-text {
  flex: 1;
  font-size: var(--fs-big);
  font-weight: 700;
  color: var(--primary-blue);
}

.checkmark-icon {
  flex-shrink: 0;
  margin-left: auto;
}

/* Dropdown animation */
.dropdown-enter-active {
  transition: all 0.2s ease;
}

.dropdown-leave-active {
  transition: all 0.15s ease;
}

.dropdown-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}

.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .select-trigger {
    padding: 8px 10px;
    gap: 6px;
    min-height: 36px;
  }

  .select-dropdown {
    min-width: 180px;
  }

  .select-text {
    font-size: var(--fs-small);
  }
}
</style>
